import { DateTime } from 'luxon';
import { Booking } from './booking';
import { DayWithHours } from './agendaConfiguration';

export type RecurrenceWeeklyFields = {
  sunday: boolean;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
};

type RecurrenceMonthlyFields = {
  dayOfMonth: number;
};

type RecurrenceMonthlyByWeekFields = {
  weekDay: number;
  nthWeekDayOfMonth: number;
};

type RecurrenceOptionsWeekly = {
  frecuencyType: 'weekly';
  frecuency: number;
  hour: string;
  expireDate?: Date;
  count?: number;
} & RecurrenceWeeklyFields;

type RecurrenceOptionsMonthly = {
  frecuencyType: 'monthly';
  frecuency: number;
  hour: string;
  expireDate?: Date;
  count?: number;
} & RecurrenceMonthlyFields;

type RecurrenceOptionsMonthlyByWeek = {
  frecuencyType: 'monthly_by_week';
  frecuency: number;
  hour: string;
  expireDate?: Date;
  count?: number;
} & RecurrenceMonthlyByWeekFields;

export type RecurrenceOptions =
  | RecurrenceOptionsWeekly
  | RecurrenceOptionsMonthly
  | RecurrenceOptionsMonthlyByWeek;

export type RecurrenceWeeklyAndMonthlyOptions = {
  frecuencyType: 'monthly' | 'weekly' | 'monthly_by_week';
  frecuency: number;
  hour: string;
  expireDate?: Date;
  count?: number;
} & RecurrenceWeeklyFields &
  RecurrenceMonthlyFields &
  RecurrenceMonthlyByWeekFields;

export type Recurrence = {
  id: number;
  userId: string;
  agendaConfigurationId: number;
  patientId?: number;
  bookingName: string;
  bookingEmail: string;
  bookingPhone?: string;
  bookingComments?: string;
  timeZone: string;
} & RecurrenceOptions;

export type CreateRecurrence = Omit<Recurrence, 'id' | 'userId' | 'timeZone'> & {
  onSite: boolean;
  startDate: Date;
};

export type RecurrenceDayWithHours = {
  name: string;
  recurrenceId: number;
  agendaConfigurationId: number;
} & DayWithHours;

type CreateRecurrenceResponse = {
  recurrence: Recurrence;
  booking: Booking;
};

export interface RecurrenceMixin {
  clientTimezone?: string | undefined;
  fetch: (path: string, init?: RequestInit) => Promise<any>;
  recurrenceList: () => Promise<Recurrence[]>;
  getRecurrence(id: number): Promise<Recurrence>;
  allRecurrenceDates(
    startDate: Date,
    endDate: Date,
    extension?: string
  ): Promise<RecurrenceDayWithHours[]>;
  createRecurrence: (recurrence: CreateRecurrence) => Promise<CreateRecurrenceResponse>;
}
export const RecurrenceMixin = {
  async fetch(path: string, init?: RequestInit): Promise<any> {
    throw new Error('Not implemented');
  },
  async recurrenceList(): Promise<Recurrence[]> {
    const res: Recurrence[] = await this.fetch('/recurrences');

    return res.map((recurrence) => {
      return {
        ...recurrence,
        expireDate: recurrence?.expireDate ? new Date(recurrence.expireDate) : undefined,
      };
    });
  },
  async getRecurrence(id: number): Promise<Recurrence> {
    const recurrence: Recurrence = await this.fetch(`/recurrences/${id}`);
    return {
      ...recurrence,
      expireDate: recurrence?.expireDate ? new Date(recurrence.expireDate) : undefined,
    };
  },
  async allRecurrenceDates(
    startDate: Date,
    endDate: Date,
    extension?: string
  ): Promise<RecurrenceDayWithHours[]> {
    const res: RecurrenceDayWithHours[] = await this.fetch(
      `/all_dates_from_recurrences?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}${extension ? '&extension=' + extension : ''}`,
      { method: 'get' }
    );
    return res;
  },
  async createRecurrence(recurrence: CreateRecurrence): Promise<CreateRecurrenceResponse> {
    const timeZone = this.clientTimezone ? this.clientTimezone : DateTime.local().zoneName;
    const res: CreateRecurrenceResponse = await this.fetch('/recurrences', {
      method: 'post',
      body: JSON.stringify({
        ...recurrence,
        timeZone: timeZone,
      }),
    });
    return {
      booking: { ...res.booking, startDate: new Date(res.booking.startDate) },
      recurrence: {
        ...res.recurrence,
        expireDate: res.recurrence?.expireDate ? new Date(res.recurrence.expireDate) : undefined,
      },
    };
  },
} as RecurrenceMixin;
